import type { FC, ReactNode } from 'react'
import type { ArticleSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { formatDate, initials } from '@/lib/format'
import { LiveDot } from '@/components/generals/live-dot'
import { GradientCover } from '@/components/journal/gradient-cover'

export const CategoryPill: FC<{ category: string }> = ({ category }): ReactNode => {
    return (
        <span className='eyebrow inline-flex items-center rounded-full bg-cream px-2.5 py-1 text-ink-soft'>
            {category}
        </span>
    )
}

interface ArticleCardProps {
    article: ArticleSummary
}

export const ArticleCard: FC<ArticleCardProps> = ({ article }): ReactNode => {
    return (
        <Link
            href={`/journal/${article.slug}`}
            className='surface card-warm group/article flex h-full flex-col overflow-hidden rounded-[26px] border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine'
        >
            <GradientCover
                seed={article.slug}
                color={article.coverColor}
                image={article.coverImage}
                imageAlt={article.coverImageAlt}
                label={article.category}
                className='aspect-[16/10] w-full'
            />

            <span className='flex flex-1 flex-col p-6'>
                <span>
                    <CategoryPill category={article.category} />
                </span>

                <span className='mt-4 block break-words font-heading text-xl font-semibold leading-snug tracking-[-0.025em] text-ink transition-colors duration-300 group-hover/article:text-ultramarine'>
                    {article.title}
                </span>

                <span className='mt-3 line-clamp-3 block text-[14.5px] leading-relaxed text-ink-soft'>
                    {article.excerpt}
                </span>

                <span className='eyebrow mt-auto flex items-center justify-between gap-3 pt-6 text-stone'>
                    <span>
                        {formatDate(article.publishedOn)}
                    </span>

                    <span>
                        {`${article.readMinutes} min read`}
                    </span>
                </span>
            </span>
        </Link>
    )
}

export const FeaturedArticle: FC<ArticleCardProps> = ({ article }): ReactNode => {
    return (
        <Link
            href={`/journal/${article.slug}`}
            className='surface card-warm group/featured grid overflow-hidden rounded-[32px] border border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine lg:grid-cols-[1.15fr_1fr]'
        >
            <GradientCover
                seed={article.slug}
                color={article.coverColor}
                image={article.coverImage}
                imageAlt={article.coverImageAlt}
                label={`Featured · ${article.category}`}
                className='aspect-[16/10] w-full lg:aspect-auto lg:min-h-[440px]'
                priority
            />

            <span className='flex flex-col p-6 sm:p-10'>
                <span className='eyebrow flex items-center gap-2 text-stone'>
                    <LiveDot />
                    Featured note
                </span>

                <span className='mt-6'>
                    <CategoryPill category={article.category} />
                </span>

                <span className='headline mt-5 block break-words text-[clamp(1.85rem,3.6vw,3rem)] text-ink transition-colors duration-300 group-hover/featured:text-ultramarine'>
                    {article.title}
                </span>

                <span className='mt-5 block text-[16px] leading-relaxed text-ink-soft sm:text-lg'>
                    {article.excerpt}
                </span>

                <span className='mt-auto flex flex-wrap items-center justify-between gap-4 pt-10'>
                    <span className='flex items-center gap-3'>
                        <span aria-hidden='true' className='grid size-10 place-items-center rounded-full bg-cream text-[12px] font-semibold text-ink-soft'>
                            {initials(article.authorName)}
                        </span>

                        <span>
                            <span className='block text-[14px] font-semibold text-ink'>
                                {article.authorName}
                            </span>

                            <span className='block text-[13px] text-stone'>
                                {`${formatDate(article.publishedOn)} · ${article.readMinutes} min read`}
                            </span>
                        </span>
                    </span>

                    <span className='inline-flex items-center gap-1.5 text-sm font-medium text-ultramarine'>
                        Read the note
                        <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover/featured:translate-x-1' />
                    </span>
                </span>
            </span>
        </Link>
    )
}
